import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { ServiceOrderState } from '@horology/shared-types';
import {
  addOrderItem,
  deleteOrderItem,
  deleteOrderPhoto,
  getServiceOrder,
  pdfUrl,
  transitionOrder,
  uploadOrderPhotos,
} from './os.api';
import { ALLOWED_TRANSITIONS, STATE_COLORS, STATE_LABELS, STATE_ORDER } from './state-machine';
import { listProducts } from '../products/products.api';
import { listWarehouses } from '../warehouses/warehouses.api';
import { formatCop, imageUrl } from '../../lib/format';
import { hasPermission, useMe } from '../../hooks/useAuth';
import { readApiError } from '../../lib/api';

export function OrderDetailPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const orderId = Number(params.id);
  const me = useMe();
  const canTransition = hasPermission(me.data, 'os.transition');
  const canUpdate = hasPermission(me.data, 'os.update');

  const qc = useQueryClient();
  const order = useQuery({
    queryKey: ['service-order', orderId],
    queryFn: () => getServiceOrder(orderId),
    enabled: Number.isInteger(orderId),
  });

  const [error, setError] = useState<string | null>(null);
  const [showAddItem, setShowAddItem] = useState(false);

  const transitionMut = useMutation({
    mutationFn: (vars: { toState: ServiceOrderState; comment?: string; warehouseId?: number }) =>
      transitionOrder(orderId, vars.toState, vars.comment, vars.warehouseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-order', orderId] }),
    onError: (err) => setError(readApiError(err).message),
  });

  const uploadPhotosMut = useMutation({
    mutationFn: (files: File[]) => uploadOrderPhotos(orderId, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-order', orderId] }),
    onError: (err) => setError(readApiError(err).message),
  });

  const deletePhotoMut = useMutation({
    mutationFn: (photoId: number) => deleteOrderPhoto(orderId, photoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-order', orderId] }),
  });

  const addItemMut = useMutation({
    mutationFn: (body: Parameters<typeof addOrderItem>[1]) => addOrderItem(orderId, body),
    onSuccess: () => {
      setShowAddItem(false);
      void qc.invalidateQueries({ queryKey: ['service-order', orderId] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const deleteItemMut = useMutation({
    mutationFn: (itemId: number) => deleteOrderItem(orderId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-order', orderId] }),
  });

  if (order.isLoading) return <p>Cargando...</p>;
  if (order.isError || !order.data) return <p className="error">No se pudo cargar la orden.</p>;

  const o = order.data;
  const allowed = ALLOWED_TRANSITIONS[o.state] ?? [];

  const subtotal = o.items.reduce(
    (s, i) => s + Number(i.quantity) * Number(i.unitPrice),
    0,
  );

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <Link to="/orders" className="btn">← Órdenes</Link>
        <h1 style={{ margin: 0 }}>OS {o.folio}</h1>
        <span
          style={{
            background: STATE_COLORS[o.state],
            color: '#fff',
            padding: '4px 12px',
            borderRadius: 999,
            fontSize: '0.85rem',
          }}
        >
          {STATE_LABELS[o.state]}
        </span>
      </div>

      {error && <p className="error">{error}</p>}

      <Pipeline state={o.state} />

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1rem', marginTop: '1rem' }}>
        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Cliente</h3>
            <p style={{ margin: 0 }}>
              <strong>{o.customer.firstName} {o.customer.lastName}</strong> — {o.customer.idDocument}
              <br />
              <small style={{ color: 'var(--color-muted)' }}>{o.customer.phone} · {o.customer.email}</small>
            </p>
          </div>

          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Reloj</h3>
            <p style={{ margin: 0 }}>
              <strong>{o.brand} {o.model}</strong>
              {o.serial && <> · S/N {o.serial}</>}
              {o.watchKind && <> · {o.watchKind}</>}
            </p>
            <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap', color: 'var(--color-muted)' }}>
              <strong>Defectos:</strong> {o.reportedDefects}
            </p>
            <p style={{ whiteSpace: 'pre-wrap', color: 'var(--color-muted)' }}>
              <strong>Trabajos:</strong> {o.worksToPerform}
            </p>
          </div>

          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Items</h3>
              {canUpdate && !['DELIVERED', 'CANCELLED'].includes(o.state) && (
                <button className="btn" onClick={() => setShowAddItem(true)}>+ Agregar</button>
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', fontSize: '0.85rem', color: 'var(--color-muted)' }}>
                  <th style={{ padding: '0.4rem 0' }}>Concepto</th>
                  <th>Cant.</th>
                  <th>Unitario</th>
                  <th>Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {o.items.map((it) => (
                  <tr key={it.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.4rem 0' }}>
                      {it.description ?? it.product.name}
                      {it.isLabor && <em style={{ color: 'var(--color-muted)' }}> (mano de obra)</em>}
                      {it.reservedAt && !it.consumedAt && <span style={{ color: 'var(--color-accent)' }}> · reservado</span>}
                      {it.consumedAt && <span style={{ color: 'var(--color-muted)' }}> · consumido</span>}
                    </td>
                    <td>{it.quantity}</td>
                    <td>{formatCop(it.unitPrice)}</td>
                    <td>{formatCop(Number(it.quantity) * Number(it.unitPrice))}</td>
                    <td>
                      {canUpdate && !it.consumedAt && (
                        <button
                          className="btn"
                          style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                          onClick={() => deleteItemMut.mutate(it.id)}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {o.items.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: '1rem', color: 'var(--color-muted)', textAlign: 'center' }}>
                      Sin items asignados.
                    </td>
                  </tr>
                )}
              </tbody>
              {o.items.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td colSpan={3} style={{ padding: '0.5rem 0', textAlign: 'right' }}><strong>Subtotal items</strong></td>
                    <td colSpan={2}><strong>{formatCop(subtotal)}</strong></td>
                  </tr>
                </tfoot>
              )}
            </table>
            {showAddItem && (
              <AddItemModal
                onCancel={() => setShowAddItem(false)}
                onSubmit={(b) => addItemMut.mutate({ ...b, productId: b.productId })}
                submitting={addItemMut.isPending}
              />
            )}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Fotos ({o.photos.length}/2)</h3>
              {canUpdate && o.photos.length < 4 && (
                <label className="btn">
                  + Agregar foto
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadPhotosMut.mutate([f]);
                    }}
                  />
                </label>
              )}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '0.5rem',
                marginTop: '0.75rem',
              }}
            >
              {[1, 2, 3, 4].map((slot) => {
                const ph = o.photos.find((p) => p.slot === slot);
                return (
                  <div
                    key={slot}
                    style={{
                      aspectRatio: '1 / 1',
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--color-border)',
                      backgroundImage: ph ? `url(${imageUrl(ph.path)})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'var(--color-muted)',
                      fontSize: '0.85rem',
                      position: 'relative',
                    }}
                  >
                    {!ph && <span>Slot {slot}</span>}
                    {ph && canUpdate && (
                      <button
                        onClick={() => deletePhotoMut.mutate(ph.id)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '2px 6px',
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Acciones</h3>
            {canTransition && allowed.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {allowed.map((to) => (
                  <TransitionButton
                    key={to}
                    from={o.state}
                    to={to}
                    onSubmit={(comment, warehouseId) =>
                      transitionMut.mutate({ toState: to, comment, warehouseId })
                    }
                  />
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--color-muted)', margin: 0 }}>Sin transiciones disponibles.</p>
            )}
          </div>

          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Documentos</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              <a className="btn" href={pdfUrl(o.id, 'letter')} target="_blank" rel="noreferrer">
                PDF Carta
              </a>
              <a className="btn" href={pdfUrl(o.id, 'half-letter')} target="_blank" rel="noreferrer">
                PDF Media Carta
              </a>
              <a className="btn" href={pdfUrl(o.id, 'ticket')} target="_blank" rel="noreferrer">
                PDF Ticket 80mm
              </a>
              <a
                className="btn"
                href={pdfUrl(o.id, 'label')}
                target="_blank"
                rel="noreferrer"
                title="Etiqueta adhesiva 60×40mm para identificar el reloj"
              >
                🏷️ Etiqueta 60×40mm
              </a>
            </div>
            <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--color-muted)' }}>
              <strong>QR público:</strong>
              <br />
              <code style={{ wordBreak: 'break-all', fontSize: '0.7rem' }}>
                /os/{o.signedToken.slice(0, 32)}...
              </code>
            </p>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>Histórico de estados</h3>
            <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
              {o.stateLogs.map((log) => (
                <li key={log.id} style={{ marginBottom: '0.4rem' }}>
                  <strong>{STATE_LABELS[log.toState]}</strong>
                  <br />
                  <small style={{ color: 'var(--color-muted)' }}>
                    {new Date(log.createdAt).toLocaleString('es-CO')} · {log.changedBy.fullName}
                  </small>
                  {log.comment && <div style={{ fontStyle: 'italic' }}>{log.comment}</div>}
                </li>
              ))}
            </ul>
          </div>

          {o.invoice && (
            <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--color-accent)' }}>
              <h3 style={{ marginTop: 0 }}>Factura</h3>
              <p style={{ margin: 0 }}>
                <strong>{o.invoice.number}</strong> — {formatCop(o.invoice.total)}
              </p>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function Pipeline({ state }: { state: ServiceOrderState }) {
  const idx = STATE_ORDER.indexOf(state);
  const closed = state === 'CANCELLED' || state === 'ABANDONED';

  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.75rem' }}>
      {STATE_ORDER.map((s, i) => {
        const reached = !closed && i <= idx;
        const current = !closed && i === idx;
        return (
          <div key={s} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: reached ? STATE_COLORS[s] : 'var(--color-border)',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontSize: '0.75rem',
                fontWeight: 600,
                outline: current ? `3px solid ${STATE_COLORS[s]}33` : 'none',
              }}
            >
              {i + 1}
            </div>
            <div style={{ flex: 1, marginLeft: '0.4rem', fontSize: '0.75rem', color: reached ? 'var(--color-text)' : 'var(--color-muted)' }}>
              {STATE_LABELS[s]}
            </div>
            {i < STATE_ORDER.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: i < idx && !closed ? STATE_COLORS[s] : 'var(--color-border)',
                  margin: '0 0.25rem',
                }}
              />
            )}
          </div>
        );
      })}
      {closed && (
        <span
          style={{
            background: STATE_COLORS[state],
            color: '#fff',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: '0.75rem',
            marginLeft: '0.5rem',
          }}
        >
          {STATE_LABELS[state]}
        </span>
      )}
    </div>
  );
}

function TransitionButton(props: {
  from: ServiceOrderState;
  to: ServiceOrderState;
  onSubmit: (comment?: string, warehouseId?: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const requiresWarehouse = props.to === 'IN_REPAIR';
  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: listWarehouses,
    enabled: open && requiresWarehouse,
  });
  const [comment, setComment] = useState('');
  const [warehouseId, setWarehouseId] = useState<number | null>(null);

  if (!open) {
    return (
      <button
        className="btn"
        style={{ background: STATE_COLORS[props.to], color: '#fff', borderColor: STATE_COLORS[props.to] }}
        onClick={() => setOpen(true)}
      >
        → {STATE_LABELS[props.to]}
      </button>
    );
  }

  return (
    <div style={{ border: '1px solid var(--color-border)', padding: '0.5rem', borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
        Mover a {STATE_LABELS[props.to]}
      </div>
      {requiresWarehouse && (
        <div className="field" style={{ marginTop: '0.4rem' }}>
          <label>Bodega para reservar stock</label>
          <select
            className="input"
            value={warehouseId ?? ''}
            onChange={(e) => setWarehouseId(Number(e.target.value))}
          >
            <option value="">—</option>
            {warehouses.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
      )}
      <textarea
        className="input"
        rows={2}
        placeholder="Comentario (opcional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        style={{ marginTop: '0.4rem' }}
      />
      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
        <button
          className="btn btn-primary"
          disabled={requiresWarehouse && !warehouseId}
          onClick={() => {
            props.onSubmit(comment || undefined, warehouseId ?? undefined);
            setOpen(false);
          }}
        >
          Confirmar
        </button>
        <button className="btn" onClick={() => setOpen(false)}>Cancelar</button>
      </div>
    </div>
  );
}

function AddItemModal(props: {
  onCancel: () => void;
  onSubmit: (b: {
    productId: number;
    description?: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
    isLabor: boolean;
  }) => void;
  submitting: boolean;
}) {
  const products = useQuery({
    queryKey: ['products', { limit: 200 }],
    queryFn: () => listProducts({ limit: 100 }),
  });
  const [productId, setProductId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('0');
  const [taxRate, setTaxRate] = useState('19');
  const [description, setDescription] = useState('');
  const [isLabor, setIsLabor] = useState(false);

  const onSelectProduct = (id: number) => {
    setProductId(id);
    const p = products.data?.data.find((x) => x.id === id);
    if (p) {
      setUnitPrice(String(p.salePrice));
      setTaxRate(String(p.taxRate));
      setIsLabor(p.isService);
      if (!description) setDescription(p.name);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
    >
      <div className="card" style={{ width: 480 }}>
        <h3 style={{ marginTop: 0 }}>Agregar item</h3>
        <div className="field">
          <label>Producto / Servicio</label>
          <select
            className="input"
            value={productId ?? ''}
            onChange={(e) => onSelectProduct(Number(e.target.value))}
          >
            <option value="">—</option>
            {products.data?.data.map((p) => (
              <option key={p.id} value={p.id}>
                {p.sku} — {p.name} {p.isService ? '(servicio)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Descripción</label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Cantidad</label>
            <input
              className="input"
              type="number"
              step="0.001"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Unitario</label>
            <input
              className="input"
              type="number"
              step="0.01"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
            />
          </div>
          <div className="field">
            <label>IVA (%)</label>
            <input
              className="input"
              type="number"
              step="0.01"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
            />
          </div>
        </div>
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <input type="checkbox" checked={isLabor} onChange={(e) => setIsLabor(e.target.checked)} />
          Mano de obra (no afecta stock)
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            className="btn btn-primary"
            disabled={!productId || props.submitting}
            onClick={() =>
              productId &&
              props.onSubmit({
                productId,
                description: description || undefined,
                quantity: Number(quantity),
                unitPrice: Number(unitPrice),
                taxRate: Number(taxRate),
                isLabor,
              })
            }
          >
            {props.submitting ? 'Agregando...' : 'Agregar'}
          </button>
          <button className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
